/**
 * SpecialReportsTab — Phase 3.1 field-mapping engine UI.
 *
 * Wired into the Writing hub's "Special reports" tab. Workflow:
 *   1. Pick a template (only WorkSafe VIC AHRMP for now).
 *   2. Pick a client (typeahead against `/exercise-programs/bulk/search-clients`).
 *   3. We call `/api/special-reports/prefill` to fetch auto-derived values
 *      + a `provenance` map (which source each field came from).
 *   4. The form renders section-by-section — text, textarea, date,
 *      checkbox, and yes/no fields. Fields with a provenance badge show
 *      where the value was read from ("Client profile", "Latest note", etc.).
 *   5. Operator edits freely, then hits "Download PDF" (editable) or
 *      "Download final" (read-only for signing/fax).
 *
 * Drafts are optional — the current MVP surfaces the flow end-to-end;
 * per-client draft persistence is wired at the backend and can be surfaced
 * in a follow-up if the operator asks for it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileCheck2, Search, Download, Sparkles, Loader2, X, User, Stethoscope, ClipboardCheck, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Small human-readable label for each derivation source. Keep in sync with
// backend's `_resolve()` head keys.
const PROVENANCE_LABEL = {
  "client_profile": { label: "Client profile", icon: User, tone: "bg-blue-50 border-blue-200 text-blue-800" },
  "practitioner":   { label: "Signature settings", icon: Stethoscope, tone: "bg-emerald-50 border-emerald-200 text-emerald-800" },
  "latest_note":    { label: "Latest note", icon: ClipboardCheck, tone: "bg-violet-50 border-violet-200 text-violet-800" },
  "latest_note.ai": { label: "AI suggestion", icon: Sparkles, tone: "bg-amber-50 border-amber-200 text-amber-900" },
  "today":          { label: "Today", icon: CalendarClock, tone: "bg-zinc-50 border-zinc-200 text-zinc-700" },
};

function provenanceKey(deriv) {
  if (!deriv) return null;
  // Preserve the "latest_note.ai" variant so AI-derived fields get their
  // own amber "AI suggestion" chip. Everything else collapses to the head.
  if (deriv === "latest_note.ai") return "latest_note.ai";
  return deriv.split(".")[0];
}

export default function SpecialReportsTab() {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [schema, setSchema] = useState(null);
  const [client, setClient] = useState(null); // {client_email, client_name}
  const [values, setValues] = useState({});
  const [provenance, setProvenance] = useState({});
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [aiSuggesting, setAiSuggesting] = useState(false);

  const templateKind = schema?.kind || "pdf";

  // 1) Load available templates once.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/special-reports/templates");
        const list = r.data?.templates || [];
        setTemplates(list);
        if (list.length === 1) setTemplateId(list[0].id);
      } catch (e) {
        toast.error("Couldn't load report templates", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    })();
  }, []);

  // 2) When the template changes, load its full field schema.
  useEffect(() => {
    if (!templateId) { setSchema(null); return; }
    (async () => {
      setLoadingSchema(true);
      try {
        const r = await api.get(`/special-reports/templates/${templateId}`);
        setSchema(r.data);
        setValues({});
        setProvenance({});
      } catch (e) {
        toast.error("Couldn't load template", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally { setLoadingSchema(false); }
    })();
  }, [templateId]);

  // 3) When the client changes, prefill from our stored data.
  const doPrefill = useCallback(async (email) => {
    if (!email || !templateId) return;
    setPrefilling(true);
    try {
      const r = await api.post("/special-reports/prefill", {
        template_id: templateId,
        client_email: email,
      });
      setValues(r.data?.values || {});
      setProvenance(r.data?.provenance || {});
      const n = Object.keys(r.data?.values || {}).length;
      toast.success(`Prefilled ${n} field${n === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("Prefill failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setPrefilling(false); }
  }, [templateId]);

  useEffect(() => {
    if (client?.client_email) doPrefill(client.client_email);
  }, [client, doPrefill]);

  // AI suggest — the LLM reads the client's recent SOAP notes and fills
  // the clinically-rich fields (diagnosis, treatment strategies, functional
  // limitations, goals, outcome-measure scores). Only replaces fields the
  // user hasn't manually touched — we merge on top of current values but
  // never overwrite a value the user has already edited (provenance ==
  // undefined means user-owned).
  async function doAiSuggest() {
    if (!client?.client_email || !templateId) return;
    setAiSuggesting(true);
    try {
      const r = await api.post("/special-reports/ai-suggest", {
        template_id: templateId,
        client_email: client.client_email,
      });
      const suggested = r.data?.values || {};
      const suggestedProv = r.data?.provenance || {};
      const noteCount = r.data?.note_count || 0;
      if (noteCount === 0) {
        toast.warning("No finalised treatment notes for this client.", {
          description: r.data?.detail || "Add a SOAP note first, then try again.",
        });
        return;
      }
      // Merge suggestions in — skip keys the user has edited (no provenance
      // set on them from a prefill and not currently blank).
      setValues((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(suggested)) {
          const isUserEdit = prev[k] && !provenance[k];
          if (!isUserEdit) next[k] = v;
        }
        return next;
      });
      setProvenance((prev) => {
        const next = { ...prev };
        for (const [k, deriv] of Object.entries(suggestedProv)) {
          // Don't overwrite an existing user-edit provenance (which is unset).
          const isUserEdit = values[k] && !prev[k];
          if (!isUserEdit) next[k] = deriv;
        }
        return next;
      });
      const filledCount = Object.keys(suggested).length;
      toast.success(`AI suggested ${filledCount} field${filledCount === 1 ? "" : "s"} from ${noteCount} recent note${noteCount === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error("AI suggest failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setAiSuggesting(false); }
  }

  // 4) Download filled PDF (editable or read-only) or filled DOCX.
  async function download(output) {
    if (!templateId) return;
    setDownloading(output);
    try {
      const r = await api.post(
        "/special-reports/generate",
        { template_id: templateId, values, output },
        { responseType: "blob" },
      );
      // Trust the server's content-type so docx vs pdf save with the right MIME.
      const ct = r.headers?.["content-type"] || "application/octet-stream";
      const blob = new Blob([r.data], { type: ct });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `${templateId}.${output === "docx" ? "docx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const label = {
        acroform: "editable PDF",
        flat: "final PDF",
        docx: "Word document",
        pdf: "PDF",
      }[output] || "file";
      toast.success(`Downloaded ${label}`);
    } catch (e) {
      toast.error("Download failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setDownloading(""); }
  }

  function patchField(fieldKey, next) {
    setValues((prev) => ({ ...prev, [fieldKey]: next }));
    // Manual edit clears provenance for that field — user has taken ownership.
    setProvenance((prev) => {
      if (!(fieldKey in prev)) return prev;
      const clone = { ...prev };
      delete clone[fieldKey];
      return clone;
    });
  }

  const totalFields = useMemo(
    () => (schema?.sections || []).reduce((n, s) => n + s.fields.length, 0),
    [schema],
  );
  const filledCount = useMemo(
    () => Object.values(values).filter((v) => v !== undefined && v !== "" && v !== false).length,
    [values],
  );

  return (
    <Card className="p-5 border-zinc-200 shadow-none space-y-5" data-testid="special-reports-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
            <FileCheck2 className="h-5 w-5" /> Special reports
          </div>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Prefill a compensable-claim report from your stored client data + signature settings.
            Review, edit, then export as an editable AcroForm PDF (for digital sign-off) or a
            read-only final PDF (for fax/email submission).
          </p>
        </div>
        {schema && (
          <div className="text-xs text-zinc-500">
            <span className="font-medium text-zinc-800">{filledCount}</span> of {totalFields} fields filled
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Report</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger data-testid="sr-template-select" className="mt-1">
              <SelectValue placeholder="Pick a report template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`sr-template-${t.id}`}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Client</Label>
          <ClientPicker
            value={client}
            onChange={setClient}
            disabled={!templateId}
          />
        </div>
      </div>

      {loadingSchema && (
        <div className="text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading fields…
        </div>
      )}

      {prefilling && (
        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-center gap-2"
             data-testid="sr-prefilling">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Prefilling from client profile, signature and latest note…
        </div>
      )}

      {schema && (
        <div className="space-y-4">
          {schema.sections.map((sec) => (
            <ReportSection
              key={sec.id}
              section={sec}
              values={values}
              provenance={provenance}
              onPatch={patchField}
            />
          ))}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-200">
            {templateKind === "pdf" ? (
              <>
                <Button
                  onClick={() => download("acroform")}
                  disabled={!templateId || !!downloading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="sr-download-editable"
                >
                  {downloading === "acroform"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  Download editable PDF
                </Button>
                <Button
                  onClick={() => download("flat")}
                  disabled={!templateId || !!downloading}
                  variant="outline"
                  data-testid="sr-download-final"
                >
                  {downloading === "flat"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  Download read-only PDF
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => download("docx")}
                  disabled={!templateId || !!downloading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="sr-download-docx"
                >
                  {downloading === "docx"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  Download Word document
                </Button>
                <Button
                  onClick={() => download("pdf")}
                  disabled={!templateId || !!downloading}
                  variant="outline"
                  data-testid="sr-download-docx-pdf"
                >
                  {downloading === "pdf"
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5 mr-1.5" />}
                  Download as PDF
                </Button>
              </>
            )}
            {client && (
              <Button
                variant="outline"
                onClick={doAiSuggest}
                disabled={aiSuggesting || !!downloading}
                className="border-amber-300 text-amber-900 hover:bg-amber-50"
                data-testid="sr-ai-suggest"
              >
                {aiSuggesting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                Suggest from notes (AI)
              </Button>
            )}
            {client && (
              <Button
                variant="ghost"
                onClick={() => doPrefill(client.client_email)}
                disabled={prefilling}
                data-testid="sr-refresh-prefill"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                Re-prefill from stored data
              </Button>
            )}
          </div>
          <p className="text-2xs text-zinc-500">
            {templateKind === "pdf" ? (
              <>
                <b>Editable PDF</b> keeps the form fields interactive so the client / practitioner can sign digitally.
                <b className="ml-2">Read-only PDF</b> marks every field as locked — use this once the plan is finalised.
              </>
            ) : (
              <>
                <b>Word document</b> opens in Word / Google Docs / Pages for editing and signature-image insertion.
                <b className="ml-2">As PDF</b> renders the filled form through LibreOffice server-side — ready to email or fax without a Word install. Ticked options render as ☒.
              </>
            )}
          </p>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ReportSection — renders one section of the schema
// ---------------------------------------------------------------------------
function ReportSection({ section, values, provenance, onPatch }) {
  return (
    <Card className="border-zinc-200 shadow-none rounded-md" data-testid={`sr-section-${section.id}`}>
      <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50">
        <div className="text-sm font-semibold text-zinc-900">{section.title}</div>
        {section.help && (
          <div className="text-2xs text-zinc-500 mt-0.5">{section.help}</div>
        )}
      </div>
      <div className="p-4 space-y-3">
        {section.fields.map((f) => (
          <FieldRow
            key={f.key}
            field={f}
            value={values[f.key]}
            provKey={provenanceKey(provenance[f.key])}
            onChange={(v) => onPatch(f.key, v)}
          />
        ))}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// FieldRow — one input, one label, one provenance chip
// ---------------------------------------------------------------------------
function FieldRow({ field, value, provKey, onChange }) {
  const prov = provKey && PROVENANCE_LABEL[provKey];
  const ProvIcon = prov?.icon;
  const testid = `sr-field-${field.key.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`;

  const provBadge = prov ? (
    <Badge
      variant="outline"
      className={cn("text-2xs font-normal py-0 px-1.5 gap-1", prov.tone)}
      title={`Auto-filled from ${prov.label}`}
    >
      {ProvIcon && <ProvIcon className="h-2.5 w-2.5" />}
      {prov.label}
    </Badge>
  ) : null;

  // Checkboxes come in two flavours:
  //   - `checkbox`    : a native PDF checkbox (WorkSafe)
  //   - `cell_option` : a docx cell whose label IS the option (TAC).
  // Both render as the same tick control on the frontend.
  if (field.type === "checkbox" || field.type === "cell_option") {
    return (
      <label
        className="flex items-center gap-2 text-sm cursor-pointer"
        data-testid={testid}
      >
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 rounded border-zinc-300"
        />
        <span className="text-zinc-800">{field.label}</span>
        {provBadge}
      </label>
    );
  }

  if (field.type === "yes_no") {
    return (
      <div className="flex items-center gap-3 text-sm" data-testid={testid}>
        <span className="text-zinc-800 flex-1 min-w-0">{field.label}</span>
        <div className="flex items-center gap-1">
          {["Yes", "No"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(value === opt ? "" : opt)}
              className={cn(
                "text-xs px-2.5 py-0.5 rounded border transition",
                value === opt
                  ? "bg-zinc-900 border-zinc-900 text-white"
                  : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50",
              )}
              data-testid={`${testid}-${opt.toLowerCase()}`}
            >
              {opt}
            </button>
          ))}
        </div>
        {provBadge}
      </div>
    );
  }

  const commonInput = {
    value: value == null ? "" : String(value),
    onChange: (e) => onChange(e.target.value),
    className: "text-sm",
    "data-testid": testid,
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-xs text-zinc-700">{field.label}</Label>
        {provBadge}
      </div>
      {field.type === "textarea" ? (
        <Textarea rows={3} {...commonInput} />
      ) : field.type === "date" ? (
        <Input type="text" placeholder="dd/mm/yyyy" {...commonInput} />
      ) : (
        <Input type="text" {...commonInput} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ClientPicker — reuses the bulk search endpoint for typeahead
// ---------------------------------------------------------------------------
function ClientPicker({ value, onChange, disabled }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      const needle = q.trim();
      if (needle.length < 2 || value) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const r = await api.post("/exercise-programs/bulk/search-clients", { q: needle });
        setResults(r.data?.results || []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, value]);

  if (value) {
    return (
      <div className="flex items-center gap-2 mt-1" data-testid="sr-client-selected">
        <div className="flex-1 min-w-0 px-3 py-2 border border-zinc-300 rounded bg-white">
          <div className="text-sm text-zinc-900 truncate">{value.client_name || "Unnamed"}</div>
          <div className="text-2xs text-zinc-500 truncate">{value.client_email}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { onChange(null); setQ(""); }}
          data-testid="sr-client-clear"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <div className="flex items-center gap-2 border border-zinc-300 rounded bg-white px-2">
        <Search className="h-3.5 w-3.5 text-zinc-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={disabled}
          placeholder="Search by name or email…"
          className="h-9 flex-1 text-sm outline-none bg-transparent"
          data-testid="sr-client-search"
          onFocus={() => setOpen(true)}
        />
        {searching && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
      </div>
      {open && results.length > 0 && (
        <div
          className="absolute z-30 mt-1 w-full max-h-[260px] overflow-y-auto border border-zinc-200 rounded bg-white shadow-md"
          data-testid="sr-client-results"
        >
          {results.map((r) => (
            <button
              key={r.client_email}
              type="button"
              onClick={() => { onChange(r); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-zinc-50 border-b last:border-0 border-zinc-100"
              data-testid={`sr-client-option-${(r.client_email || "").toLowerCase()}`}
            >
              <div className="text-sm text-zinc-900 truncate">{r.client_name || "Unnamed"}</div>
              <div className="text-2xs text-zinc-500 truncate">{r.client_email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
